import { useRef, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { referralsService } from '@/services/referralsService'
import { extractApiError } from '@/utils/extractApiError'
import { PARCEIROS_ACCENT } from './parceirosBrand'
import { ParceirosImportResult, type ImportFailure } from './ParceirosImportResult'

export const INDICACOES_TEMPLATE_URL = '/templates/indicacoes-parceiros.xlsx'
const INDICACOES_SHEET_IMAGE = '/templates/indicacoes-planilha.svg'

type PartnerOption = { id: string; nome: string; empresa?: string | null }

type Props = {
  open: boolean
  onClose: () => void
  onImported: () => Promise<void> | void
  /** No CRM, o closer escolhe o parceiro. A indicação fica no nome dele. */
  partners?: PartnerOption[]
}

export function ParceirosImportModal({ open, onClose, onImported, partners }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const resultRef = useRef<HTMLDivElement>(null)
  const [parceiroId, setParceiroId] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [over, setOver] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [imported, setImported] = useState<number | null>(null)
  const [failed, setFailed] = useState<ImportFailure[]>([])
  const [repeated, setRepeated] = useState(0)

  function take(next: File | null | undefined) {
    setError('')
    setImported(null)
    setFailed([])
    setRepeated(0)
    if (!next) return
    if (!next.name.toLowerCase().endsWith('.xlsx')) {
      setFile(null)
      setError('Escolha uma planilha .xlsx.')
      return
    }
    setFile(next)
  }

  async function send() {
    if (!file) return
    if (partners && !parceiroId) {
      setError('Selecione o parceiro.')
      return
    }
    setSending(true)
    setError('')
    try {
      const result = partners
        ? await referralsService.importStaff(file, parceiroId)
        : await referralsService.importMine(file)
      setImported(result.imported)
      setFailed(result.failed)
      setRepeated(result.repeated ?? 0)
      queueMicrotask(() => resultRef.current?.scrollIntoView({ block: 'nearest' }))
      if (result.imported > 0) await onImported()
    } catch (err) {
      setError(extractApiError(err, 'Não foi possível importar a planilha.').message)
    } finally {
      setSending(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title='Importar indicações'
      description='Arraste a planilha preenchida ou escolha o arquivo no computador.'
      size='lg'
    >
      <div className='grid gap-4'>
        {partners && (
          <label className='grid gap-1 text-xs text-[var(--font-color-label)]'>
            Parceiro
            <select
              required
              className='rounded-md border border-[var(--glass-border)] bg-transparent px-3 py-2 text-sm text-[var(--font-color-body)]'
              value={parceiroId}
              onChange={(event) => setParceiroId(event.target.value)}
            >
              <option value=''>Selecione o parceiro que enviou os leads</option>
              {partners.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.empresa && item.empresa !== item.nome ? `${item.nome} · ${item.empresa}` : item.nome}
                </option>
              ))}
            </select>
          </label>
        )}
        <img
          src={INDICACOES_SHEET_IMAGE}
          alt='Planilha com as colunas Nome do cliente, Telefone, Email (opcional), Empresa e Site (opcional). As linhas de baixo ficam em branco para preencher.'
          className='w-full rounded-xl border border-[var(--glass-border)] bg-white'
        />
        <div
          onDragOver={(event) => {
            event.preventDefault()
            setOver(true)
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(event) => {
            event.preventDefault()
            setOver(false)
            take(event.dataTransfer.files?.[0])
          }}
          className={`grid gap-3 rounded-2xl border border-dashed px-4 py-6 text-center ${
            over ? 'border-[#e91e63] bg-[#e91e63]/10' : 'border-[var(--glass-border)] bg-[var(--bg-main)]'
          }`}
        >
          <p className='text-sm text-text-primary'>Arraste o arquivo .xlsx até aqui</p>
          <p className='text-xs text-text-muted'>{file ? file.name : 'Nenhum arquivo selecionado'}</p>
          <div className='flex flex-wrap items-center justify-center gap-2'>
            <button
              type='button'
              onClick={() => inputRef.current?.click()}
              className='rounded-full border border-[var(--glass-border)] px-4 py-2 text-sm text-text-primary'
            >
              Procurar arquivo
            </button>
            <button
              type='button'
              disabled={!file || sending || Boolean(partners && !parceiroId)}
              onClick={() => void send()}
              className='rounded-full px-4 py-2 text-sm font-medium text-white disabled:opacity-50'
              style={{ background: PARCEIROS_ACCENT }}
            >
              {sending ? 'Importando…' : 'Importar planilha'}
            </button>
          </div>
          <input
            ref={inputRef}
            type='file'
            accept='.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            className='sr-only'
            onChange={(event) => {
              take(event.target.files?.[0])
              event.target.value = ''
            }}
          />
        </div>

        {error && <p className='text-sm text-red-300'>{error}</p>}

        <a
          href={INDICACOES_TEMPLATE_URL}
          download='indicacoes-parceiros.xlsx'
          className='text-sm text-text-primary underline-offset-2 hover:underline'
        >
          Baixar template (.xlsx)
        </a>

        {imported != null && (
          <div ref={resultRef}>
            <ParceirosImportResult imported={imported} failed={failed} repeated={repeated} />
          </div>
        )}
      </div>
    </Modal>
  )
}
